import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { ApiSchemaActivityByAppointmentIdAndDate } from "../../models/api";
import { ApiAppointment } from "../../core/apis";
import { TimeUtility, UtilityDate } from "../../utils";
import { DateInput } from "../textInput.component";
import { Button, Form, Modal } from "rsuite";
import { showErrorToast, showSuccessToast } from "../toast.component";

interface ITask {
  appointmentId: string;
  date: string;
  start: string;
  end: string;
}

interface IProps {
  show: boolean;
  toggleModal: () => void;
  onSave: Function;
  selectedTask: ITask;
  setSelectedTask: Dispatch<SetStateAction<ITask>>;
}

export function ModalEditCheckInCheckOut({
  show,
  toggleModal,
  selectedTask,
  setSelectedTask,
  onSave,
}: IProps) {
  const [isFetching, setIsFetching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [data, setData] =
    useState<ApiSchemaActivityByAppointmentIdAndDate | null>();
  const [formData, setFormData] = useState({
    checkInTime: new Date(),
    checkOutTime: new Date(),
  });
  const [totalTime, setTotalTime] = useState<string>("");
  const maxTime = TimeUtility.formatDecimalHoursToHM(data?.maxHours ?? 0);
  const [isOverTime, setIsOverTime] = useState<boolean>(false);
  const [isCheckoutBeforeCheckin, setIsCheckoutBeforeCheckin] =
    useState<boolean>(false);

  const handleCloseModal = () => {
    toggleModal();
    setSelectedTask({ ...selectedTask, appointmentId: "" });
  };

  useEffect(() => {
    if (!selectedTask.appointmentId || !selectedTask.date) {
      return;
    }
    const getData = async () => {
      setIsFetching(true);
      const response = await ApiAppointment.GetActivityByAppointmentIdAndDate(
        selectedTask.appointmentId,
        selectedTask.date
      );
      setData(response);
      setIsFetching(false);

      if (response?.checkedInTime) {
        const checkInTime = new Date(response.checkedInTime);
        setFormData((prev) => ({ ...prev, checkInTime }));
      }
      if (response?.checkedOutTime) {
        const checkOutTime = new Date(response.checkedOutTime);
        setFormData((prev) => ({ ...prev, checkOutTime }));
      }
    };
    getData();
  }, [selectedTask.appointmentId, selectedTask.date]);

  const handleCheckInTimeChange = (time: Date) => {
    setFormData((prev) => {
      const newData = { ...prev, checkInTime: time };
      return newData;
    });
  };

  const handleCheckOutTimeChange = (time: Date) => {
    setFormData((prev) => {
      const newData = { ...prev, checkOutTime: time };
      return newData;
    });
  };

  const onUpdate = async () => {
    setIsUpdating(true);
    const results = await Promise.allSettled([
      ApiAppointment.UpdateCheckInTime(
        selectedTask.appointmentId,
        data?.checkedInTime!,
        formData.checkInTime.toISOString()
      ),
      ApiAppointment.UpdateCheckOutTime(
        selectedTask.appointmentId,
        data?.checkedOutTime!,
        formData.checkOutTime.toISOString()
      ),
    ]);
    const [checkInResponse, checkOutResponse] = results;
    if (
      checkInResponse.status === "fulfilled" &&
      checkOutResponse.status === "fulfilled" &&
      checkInResponse.value?.isSuccess &&
      checkOutResponse.value?.isSuccess
    ) {
      showSuccessToast("Successfully updated");
      onSave(selectedTask.appointmentId);
      toggleModal();
    } else {
      showErrorToast({ isSuccess: false, data: "Failed to update" });
    }
    setIsUpdating(false);
  };

  const onForceCheckout = async () => {
    setIsUpdating(true);
    const response = await ApiAppointment.CheckOutForce(
      selectedTask.appointmentId,
      selectedTask.date
    );
    if (response?.isSuccess) {
      showSuccessToast("Successfully updated");
      onSave(selectedTask.appointmentId);
      toggleModal();
    } else {
      showErrorToast({ isSuccess: false, data: "Failed to update" });
    }
    setIsUpdating(false);
  };

  const onForceCheckin = async () => {
    setIsUpdating(true);
    const response = await ApiAppointment.CheckInForce(
      selectedTask.appointmentId,
      selectedTask.date
    );
    if (response?.isSuccess) {
      showSuccessToast("Successfully updated");
      onSave(selectedTask.appointmentId);
      toggleModal();
    } else {
      showErrorToast({ isSuccess: false, data: "Failed to update" });
    }
    setIsUpdating(false);
  };

  useEffect(() => {
    if (data?.isCheckedIn && data?.isCheckedOut) {
      const diffMs =
        formData.checkOutTime.getTime() - formData.checkInTime.getTime();

      // Check if checkout is before checkin
      if (diffMs < 0) {
        setIsCheckoutBeforeCheckin(true);
        setTotalTime("Invalid time range");
        return;
      }
      setIsCheckoutBeforeCheckin(false);

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      // Check if the total time exceeds max time
      if (data?.maxHours) {
        const totalHours = parseFloat((hours + minutes / 60).toFixed(2));
        setIsOverTime(totalHours > data.maxHours);
      }

      setTotalTime(`${hours}h ${minutes}m`);
    }
  }, [
    data?.isCheckedIn,
    data?.isCheckedOut,
    formData.checkInTime,
    formData.checkOutTime,
    data?.maxHours,
  ]);

  return (
    <Modal
      open={show}
      onClose={handleCloseModal}
      className="check-in-out-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <div>
            <p className="fs-6 text-muted">Edit Check-in and Check-out </p>
            <hr />
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "80vh" }}>
        {isFetching ? (
          <div className="p-5 d-flex justify-content-center align-items-center">
            <div className="spinner-border spinner-border-sm" />
          </div>
        ) : (
          <div className="container">
            <div className="heading-container">
              <p className="heading">Appointment</p>
              <p className="small">
                Date: {UtilityDate.getDayMonthV1(selectedTask.date)} | Start
                Time: {UtilityDate.formatTime(selectedTask.start)} | End Time:{" "}
                {UtilityDate.formatTime(selectedTask.end)}
              </p>
            </div>

            <div className="info d-flex align-items-center my-4">
              <img
                src="/img/admin-profile.svg"
                alt="profile"
                width={48}
                height={48}
                className="rounded-circle object-fit-cover"
              />
              <div className="mx-2">
                <p className="fw-medium">Caregiver: {data?.caregiverName}</p>
                <p className="text-muted">
                  Phone: {data?.caregiverPhoneNumber}
                </p>
              </div>
            </div>

            <Form formValue={formData}>
              <div className="check-container ">
                <div>
                  {data?.isCheckedIn ? (
                    <DateInput
                      name="checkInTime"
                      timeChange={handleCheckInTimeChange}
                      time
                      cleanable={false}
                    >
                      Caregiver Check-in Time
                    </DateInput>
                  ) : (
                    <div className="rs-form-group">
                      <p> Caregiver Check-in Time:</p>
                      <p className="text-muted m-0"> Did not check in yet</p>
                    </div>
                  )}
                </div>

                <div>
                  {data?.isCheckedOut ? (
                    <>
                      <DateInput
                        name="checkOutTime"
                        timeChange={handleCheckOutTimeChange}
                        time
                        cleanable={false}
                      >
                        Caregiver Check-out Time
                      </DateInput>
                      {isCheckoutBeforeCheckin && (
                        <p className="text-danger small">
                          Check-out time cannot be before check-in time
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="rs-form-group">
                      <p>Caregiver Check-out Time:</p>
                      <p className="text-muted m-0">Did not check out yet</p>
                    </div>
                  )}
                </div>

                {data?.isCheckedIn && data?.isCheckedOut && (
                  <>
                    <div className="rs-form-group mt-3">
                      <p>Total Time:</p>
                      <p
                        className={`${
                          isOverTime || isCheckoutBeforeCheckin
                            ? "text-danger fw-bold"
                            : "text-muted"
                        } m-0`}
                      >
                        {totalTime}
                      </p>
                    </div>
                    <div className="rs-form-group mt-3">
                      <p>Max Time:</p>
                      <p className="text-muted m-0">{maxTime}</p>
                    </div>
                  </>
                )}
              </div>
            </Form>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!isFetching && (
          <>
            {data?.isCheckedIn && data?.isCheckedOut && (
              <Button
                onClick={onUpdate}
                appearance="primary"
                disabled={isUpdating || isCheckoutBeforeCheckin}
              >
                Update
              </Button>
            )}
            {!data?.isCheckedIn && (
              <Button
                onClick={onForceCheckin}
                className="force-checkout"
                disabled={isUpdating}
              >
                Force Clock-In
              </Button>
            )}
            {data?.isCheckedIn && !data?.isCheckedOut && (
              <Button
                onClick={onForceCheckout}
                className="force-checkout"
                disabled={isUpdating}
              >
                Force Clock-Out
              </Button>
            )}
            <Button onClick={handleCloseModal} appearance="subtle">
              Cancel
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
